import type {ThreadUserMessagePart} from '@assistant-ui/core';
import {
  AuiIf,
  ComposerPrimitive,
  QueueItemPrimitive,
  type QueueItemState,
  useAui,
  useAuiState,
} from '@assistant-ui/react';
import {
  Dialog,
  DropdownMenu,
  Icon,
  IconButton,
  type IconName,
  TagGroup,
} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  createEditor,
  Editor,
  Element as SlateElement,
  Text as SlateText,
  Transforms,
} from 'slate';
import {withHistory} from 'slate-history';
import {ReactEditor, withReact} from 'slate-react';
import {useShallow} from 'zustand/react/shallow';

import {
  assemblePromptParts,
  draftKey,
  runKey,
  useDraft,
} from '../../../agentChat/composer';
import {toAUIContentParts} from '../../../agentChat/messageConversions';
import {
  queueKey,
  useQueueRecall,
  useQueueStore,
} from '../../../agentChat/queue';
import {DEFAULT_PROJECT_NAME} from '../../../agentChat/runtime/backend';
import {
  hasMeaningfulContent,
  useActiveThread,
  useActiveThreadActiveNetworkDomains,
  useActiveThreadId,
  useActiveThreadIsCancelling,
} from '../../../agentChat/store';
import {resolveHITLSendBlock} from '../../../agentChat/tools/catalog';
import {useAgentChatReadonly} from '../../../agentChat/useAgentChatReadonly';
import {useDebounceState} from '../../../util/hooks';
import {useNavContext} from '../../NavContextProvider';
import {filterImageFiles, imagePartToDataUrl} from '../attachments/imageParts';
import {ImageThumbnail} from '../attachments/ImageThumbnail';
import {useComposerImageUpload} from '../attachments/useComposerImageUpload';
import {QUEUE_MAX_HEIGHT, QUEUE_ROW_HEIGHT} from '../constants';
import {
  useNightModeFilter,
  useNightModeRevertFilter,
} from '../useNightModeRevertFilter';
import {useTrackWBAgentComposer} from '../useTrackWBAgent';
import {WBAgentAgentSelector} from '../WBAgentAgentSelector';
import {WBAgentDebugViewToggle} from '../WBAgentDebugView';
import {WBAgentForkButton} from '../WBAgentForkButton';
import {WBAgentManageNetworkAccess} from '../WBAgentManageNetworkAccess';
import {canQueueRowSteer, queueRowRecall} from './queueRow';
import {bodyPartsToSlate, slateToBodyParts} from './slate';
import {WBAgentEditor} from './WBAgentEditor';
import {
  useRunMentionSearch,
  WBAgentRunMentionDropdown,
} from './WBAgentRunMentionDropdown';

const ComposerAction = ({
  isEmpty,
  isLocked,
  isCancelling,
  onSend,
  onStop,
}: {
  isEmpty: boolean;
  isLocked: boolean;
  isCancelling: boolean;
  onSend: () => void;
  onStop: () => void;
}) => {
  const isSendDisabled = isEmpty || isLocked;
  return (
    <div className="flex">
      <AuiIf condition={s => !s.thread.isRunning || !isEmpty}>
        <IconButton
          isDisabled={isSendDisabled}
          aria-label="send message"
          variant="primary"
          icon="up"
          onPress={onSend}
        />
      </AuiIf>
      <AuiIf condition={s => s.thread.isRunning && isEmpty}>
        <ComposerPrimitive.Cancel asChild>
          <IconButton
            aria-label="cancel query"
            icon="agent-stop"
            // Cancel requested but not yet server-confirmed; disable the stop
            // control until the poll observes the turn's terminal state.
            isDisabled={isCancelling}
            onPress={onStop}
          />
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

const CUSTOM_VOID_TYPES = new Set(['run-mention']);

export const WBAgentComposer = ({
  onStartAddImage,
}: {
  onStartAddImage: () => void;
}) => {
  const aui = useAui();
  const isRunning = useAuiState(s => s.thread.isRunning);
  const queueCount = useAuiState(s => s.composer.queue.length);
  const isCancelling = useActiveThreadIsCancelling();
  const {params} = useNavContext();
  const {entityName, projectName} = params;
  const nightModeRevertFilter = useNightModeRevertFilter();
  const nightModeFilter = useNightModeFilter();
  const isReadonly = useAgentChatReadonly();
  const {addComposerImages} = useComposerImageUpload();

  const activeNetworkDomains = useActiveThreadActiveNetworkDomains();

  // Per-turn agent config override from the (internal-only) picker. Sent with
  // the next message and otherwise ignored; resets when the composer remounts.
  const [agentConfigOverride, setAgentConfigOverride] = useState<string | null>(
    null
  );

  const [isComposerEmpty, setIsComposerEmpty] = useState(true);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [_liveQuery, debouncedMentionQuery, setMentionQuery] = useDebounceState(
    '',
    300
  );

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const mentionActive = isMentionOpen && !!entityName && !!projectName;
  const mentionRuns = useRunMentionSearch(
    entityName ?? '',
    projectName ?? '',
    mentionActive ? debouncedMentionQuery : ''
  );
  const clampedHighlightedIndex =
    mentionRuns.length === 0
      ? 0
      : Math.min(highlightedIndex, mentionRuns.length - 1);

  // The composer draft is keyed by the active thread (null == new chat), so
  // each conversation keeps its own unsent message, runs, and files.
  const activeThread = useActiveThread();
  const threadId = activeThread?.id ?? null;
  const draft = useDraft(threadId);
  // NOTE: Keep out of `ReadonlyInputs` — `useToolInteraction` gates submit on
  // readonly, so the card that clears the block could never submit.
  const isBlocked = resolveHITLSendBlock(activeThread);
  const isLocked = isReadonly || isBlocked;

  // Whether the bulk network-access manager (revoke/edit view) is open.
  const networkTriggerRef = useRef<HTMLButtonElement>(null);
  const [isManagingNetwork, setIsManagingNetwork] = useState(false);
  // Close the manager whenever its trigger stops rendering, so it can't reopen
  // on its own once the trigger comes back.
  useEffect(() => {
    if (activeNetworkDomains.length === 0 || isBlocked) {
      setIsManagingNetwork(false);
    }
  }, [activeNetworkDomains.length, isBlocked]);

  // Recall pulls a queued item back into this draft; the queued store items
  // (not AUI's text-only `queueItem`) carry the attachment count the row needs.
  // A prefill overwrites the draft, so recall is offered only when the composer
  // is empty.
  const recall = useQueueRecall(activeThread, params);
  const queueItems = useQueueStore(
    useShallow(s =>
      activeThread ? s.queues[queueKey(activeThread.id)] : undefined
    )
  );
  const queueItemsById = useMemo(
    () => new Map((queueItems ?? []).map(m => [m.id, m])),
    [queueItems]
  );
  // A steer stays pending until its cancel lands and the drain dispatches;
  // while it is, every row's force-send is disabled, so only one steer runs at
  // a time.
  const steerTargetId = useQueueStore(s =>
    activeThread != null
      ? s.steerRequested[queueKey(activeThread.id)]
      : undefined
  );
  const steerPending = steerTargetId != null;
  const draftOccupied =
    !isComposerEmpty || draft.runs.length > 0 || draft.files.length > 0;

  // NOTE: Past the cap the newest row lands off-screen, so an append would look
  // like a dropped message. Growth only, so draining doesn't yank the scroll.
  const queueListRef = useRef<HTMLDivElement>(null);
  const prevQueueCount = useRef(queueCount);
  useEffect(() => {
    const grew = queueCount > prevQueueCount.current;
    prevQueueCount.current = queueCount;
    if (grew) {
      queueListRef.current?.scrollTo({top: queueListRef.current.scrollHeight});
    }
  }, [queueCount]);

  // NOTE: A stable renderer lets `ComposerPrimitive.Queue` memoize its rows
  // through a composer render.
  const renderQueueRow = useCallback(
    ({queueItem}: {queueItem: QueueItemState}) => {
      const item = queueItemsById.get(queueItem.id);
      const {imageCount, canRecall} = queueRowRecall(item);
      const canSteer = canQueueRowSteer(item, steerPending);
      const isSteering = steerTargetId === queueItem.id;
      return (
        <div className="flex items-center gap-8 rounded border border-moon-250 bg-white px-12 py-6">
          <QueueItemPrimitive.Text className="min-w-[120px] flex-1 truncate" />
          {/* NOTE: Holds the controls at full size once the text hits its
              floor, so a narrow row scrolls rather than collapse. */}
          <div className="flex shrink-0 items-center gap-8">
            {imageCount > 0 && (
              <span
                className="flex items-center gap-2 text-moon-500"
                aria-label={`${imageCount} attached image${
                  imageCount > 1 ? 's' : ''
                }`}>
                <Icon icon="photo-picture" size="sm" color="moon" />
                {imageCount}
              </span>
            )}
            {canRecall && (
              <IconButton
                icon="pencil-edit"
                variant="ghost"
                isDisabled={draftOccupied}
                tooltip={{children: 'Edit'}}
                aria-label={`Edit queued message: ${queueItem.prompt}`}
                onPress={() => recall(queueItem.id)}
              />
            )}
            {!isSteering && (
              <QueueItemPrimitive.Steer asChild>
                <IconButton
                  icon="up"
                  variant="ghost"
                  isDisabled={!canSteer}
                  tooltip={{children: 'Run now'}}
                  aria-label={`Run queued message now: ${queueItem.prompt}`}
                />
              </QueueItemPrimitive.Steer>
            )}
            {isSteering && (
              <span
                role="status"
                aria-label="Interrupting"
                className="inline-flex animate-spin [animation-duration:4s]">
                <Icon icon="loading" size="sm" color="moon" />
              </span>
            )}
            <QueueItemPrimitive.Remove asChild>
              <IconButton
                icon="delete"
                variant="ghost"
                isDisabled={isSteering}
                aria-label={`Remove queued message: ${queueItem.prompt}`}
              />
            </QueueItemPrimitive.Remove>
          </div>
        </div>
      );
    },
    [queueItemsById, steerPending, steerTargetId, draftOccupied, recall]
  );

  const {
    trackMentionAdded,
    trackMentionRemoved,
    trackMessageSent,
    trackTurnStopped,
  } = useTrackWBAgentComposer();

  // NOTE: Keyed by the sticky ID, not the draft's rotating key, so a mid-run
  // promotion can't remount the editor and lose the caret.
  const stickyThreadId = useActiveThreadId();
  const editorKey = `${draftKey(stickyThreadId)}:${draft.nonce}`;
  const editor = useMemo(() => {
    const e = withHistory(withReact(createEditor()));
    const {isInline, isVoid} = e;
    e.isInline = element => {
      return element.type != null && CUSTOM_VOID_TYPES.has(element.type)
        ? true
        : isInline(element);
    };
    e.isVoid = element => {
      return element.type != null && CUSTOM_VOID_TYPES.has(element.type)
        ? true
        : isVoid(element);
    };
    return e;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey]);

  // NOTE: `Slate` reads `initialValue` only when it mounts, so recomputing it
  // costs nothing and reseeds a remount from the draft as it stands now.
  const initialValue = bodyPartsToSlate(draft.body);

  const getAtMatch = (editor: Editor) => {
    const {selection} = editor;
    if (!selection) {
      return null;
    }
    const [node] = Editor.node(editor, selection.anchor);
    if (!SlateText.isText(node)) {
      return null;
    }
    const textBeforeCursor = node.text.slice(0, selection.anchor.offset);
    return textBeforeCursor.match(/@([\w-]*)$/);
  };

  const handleSlateChange = useCallback(() => {
    const voidNodeEntries = [
      ...Editor.nodes(editor, {
        at: [],
        match: n =>
          SlateElement.isElement(n) &&
          n.type != null &&
          CUSTOM_VOID_TYPES.has(n.type),
      }),
    ];
    setIsComposerEmpty(
      Editor.string(editor, []).trim().length === 0 &&
        voidNodeEntries.length === 0
    );
    // Persist real edits only — a selection-only change (cursor move, or the
    // caret focus on remount) would rewrite the draft with the seeded body.
    if (editor.operations.some(op => op.type !== 'set_selection')) {
      draft.setBody(slateToBodyParts(editor.children));
    }
    const atMatch = getAtMatch(editor);
    // Run mentions require an entity/project to query against, so suppress
    // the dropdown off-project rather than intercept Enter for an empty list.
    if (atMatch && entityName && projectName) {
      setIsMentionOpen(true);
      setMentionQuery(atMatch[1]);
      setHighlightedIndex(0);
    } else {
      setIsMentionOpen(false);
      setMentionQuery('');
    }
  }, [editor, entityName, projectName, setMentionQuery, draft]);

  const handleRunSelect = (displayName: string, name: string) => {
    if (!entityName || !projectName) {
      return;
    }

    const atMatch = getAtMatch(editor);
    if (atMatch) {
      Transforms.delete(editor, {
        distance: atMatch[0].length,
        unit: 'character',
        reverse: true,
      });
    }
    Transforms.insertNodes(editor, {
      type: 'run-mention',
      displayName,
      name,
      entityName,
      projectName,
      children: [{text: ''}],
    });
    Transforms.move(editor);
    Transforms.insertText(editor, ' ');

    setIsMentionOpen(false);
    setMentionQuery('');
    ReactEditor.focus(editor);
    trackMentionAdded('composer_dropdown', 'run');
  };

  // Submit a user turn best-effort and report whether it was sent. No-op when
  // the chat is read-only, so a refused send never clears the composer and
  // callers needn't check access.
  const trySubmitTurn = (
    content: ThreadUserMessagePart[],
    runConfig: {custom: Record<string, unknown>}
  ): boolean => {
    if (isLocked) {
      return false;
    }
    aui.thread().append({role: 'user', content, runConfig});
    return true;
  };

  const handleSend = () => {
    const userParts = assemblePromptParts({
      runs: draft.runs,
      body: slateToBodyParts(editor.children),
      files: draft.files,
    });

    if (!hasMeaningfulContent(userParts)) {
      return;
    }

    const content = userParts.flatMap(
      toAUIContentParts
    ) as ThreadUserMessagePart[];
    if (!trySubmitTurn(content, {custom: {agentConfigOverride}})) {
      return;
    }
    trackMessageSent('composer');
    draft.reset();
  };

  // Run the next turn with an explicit network allowlist. Grant/revoke both
  // funnel through here: the backend patch replaces the inherited list, so we
  // always send the full desired set, and descendant turns inherit it. The
  // next turn's resolved permissions refresh `activeNetworkDomains`.
  const runTurnWithNetworkDomains = (domains: string[], prompt: string) => {
    if (isRunning) {
      return;
    }
    const content = [{type: 'text', text: prompt}] as ThreadUserMessagePart[];
    if (
      !trySubmitTurn(content, {
        custom: {
          agentConfigOverride,
          permissions: {allowed_network_domains: domains},
          shouldBypassQueue: true,
        },
      })
    ) {
      return;
    }
    trackMessageSent('composer');
  };

  // Apply a bulk edit of the active allowlist, keeping `kept` and revoking the
  // rest in a single turn. A no-op edit submits nothing.
  const updateNetworkDomains = (kept: string[]) => {
    const removed = activeNetworkDomains.filter(d => !kept.includes(d));
    if (removed.length === 0) {
      return;
    }
    runTurnWithNetworkDomains(
      kept,
      `I've revoked network access to: ${removed.join(', ')}.`
    );
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent) => {
    // Ignore IME composition events
    if (e.nativeEvent.isComposing) {
      return;
    }

    if (e.key === 'Backspace') {
      const inlineVoid = Editor.above(editor, {
        match: n =>
          SlateElement.isElement(n) &&
          Editor.isVoid(editor, n) &&
          Editor.isInline(editor, n),
      });
      if (inlineVoid) {
        e.preventDefault();
        Editor.deleteBackward(editor);
        return;
      }
    }
    if (isMentionOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setIsMentionOpen(false);
        setMentionQuery('');
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (mentionRuns.length > 0) {
          setHighlightedIndex(i => (i + 1) % mentionRuns.length);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (mentionRuns.length > 0) {
          setHighlightedIndex(i => (i > 0 ? i - 1 : mentionRuns.length - 1));
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const run = mentionRuns[clampedHighlightedIndex];
        if (run?.displayName) {
          handleRunSelect(run.displayName, run.name);
        }
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Pasted images are prioritized over accompanying text/HTML: preventDefault
  // stops Slate from also rendering the image or duplicate content into the editor.
  // When the clipboard has no image, fall through to Slate's normal text paste.
  const handleComposerImagePaste = (
    e: React.ClipboardEvent<HTMLDivElement>
  ) => {
    const fileCount = e.clipboardData?.files.length ?? 0;
    const images = filterImageFiles(e.clipboardData?.files);
    const someFilesRejected = images.length < fileCount;
    if (images.length === 0) {
      if (someFilesRejected) {
        void addComposerImages([], ['unsupported-type']);
      }
      return;
    }
    e.preventDefault();
    void addComposerImages(
      images,
      someFilesRejected ? ['unsupported-type'] : []
    );
  };

  const tagItems = [
    ...(params.projectName && params.projectName !== DEFAULT_PROJECT_NAME
      ? [
          {
            id: 'project name',
            icon: 'folder-project' as IconName,
            textValue: params.projectName,
            removable: false,
            cssTruncate: true,
          },
        ]
      : []),
    ...draft.runs.map(run => ({
      id: runKey(run),
      icon: 'running-repeat' as IconName,
      textValue: run.displayName,
      cssTruncate: true,
    })),
  ];

  return (
    <div className="relative flex min-h-0 flex-col">
      {queueCount > 0 && (
        // NOTE: When even one row can't fit, the section clips from the
        // bottom, so the count header is the last thing to disappear.
        // first:-mt-8 tucks the header into the wrapper's top padding so a
        // short window shows more of the last message.
        <div className="first:-mt-8 mb-8 flex min-h-0 flex-col gap-4 overflow-clip">
          <div className="flex shrink-0 items-center gap-4 text-moon-500 text-sm">
            <Icon icon="forward-next" size="sm" color="moon" />
            <span>QUEUED · {queueCount}</span>
          </div>
          {/* NOTE: Scrolls the rows rather than the whole panel, so the count
              header stays visible once the queue outgrows its cap. */}
          <div
            ref={queueListRef}
            style={{maxHeight: QUEUE_MAX_HEIGHT, minHeight: QUEUE_ROW_HEIGHT}}
            className="flex flex-col gap-4 overflow-auto overscroll-none [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
            <ComposerPrimitive.Queue>{renderQueueRow}</ComposerPrimitive.Queue>
          </div>
        </div>
      )}
      <ComposerPrimitive.Root className="flex shrink-0 flex-col gap-8 rounded border border-moon-250 bg-white p-12 focus-within:border-[var(--focus-ring-color)] focus-within:shadow-[0_0_0_3px_#CDF4F7]">
        {tagItems.length > 0 && (
          <div
            className="[&_[role=row]]:max-w-[300px]"
            style={{filter: nightModeRevertFilter}}>
            <TagGroup
              aria-label="composer context tags"
              items={tagItems}
              onRemove={key => {
                draft.removeRun(String(key));
                trackMentionRemoved('run');
              }}
            />
          </div>
        )}
        {(draft.files.length > 0 || draft.failedFiles.length > 0) && (
          <div className="flex flex-start gap-8 pt-4 self-stretch overflow-x-auto [scrollbar-width:thin]">
            {draft.failedFiles.map(image => (
              <ImageThumbnail
                key={image.id}
                src={image.previewUrl}
                error="Upload failed"
                onRemove={() => draft.removeFailedFile(image.id)}
              />
            ))}
            {draft.files.map(image => (
              <ImageThumbnail
                key={image.id}
                src={imagePartToDataUrl(image)}
                onRemove={() => draft.removeFile(image.id)}
              />
            ))}
          </div>
        )}
        <WBAgentEditor
          key={editorKey}
          editor={editor}
          initialValue={initialValue}
          onChange={handleSlateChange}
          onKeyDown={handleComposerKeyDown}
          onPaste={handleComposerImagePaste}
          readOnly={isLocked}
          placeholder={isBlocked ? 'Respond above to continue' : undefined}
        />
        {mentionActive && (
          <WBAgentRunMentionDropdown
            runs={mentionRuns}
            highlightedIndex={clampedHighlightedIndex}
            onHover={setHighlightedIndex}
            onSelect={handleRunSelect}
          />
        )}
        <div className="flex w-full items-center gap-4">
          {/* NOTE: overflow-x makes this a scroll container on both axes, so
              py-4 gives the focus ring room; -my-4 cancels it in layout. */}
          <div className="-my-4 flex min-w-0 flex-1 items-center gap-4 overflow-x-auto py-4 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
            <DropdownMenu.Trigger>
              <IconButton
                icon="add-new"
                variant="ghost"
                aria-label="Add content"
                isDisabled={isLocked}
              />
              <DropdownMenu.Content placement="top start">
                <DropdownMenu.Item onAction={onStartAddImage}>
                  <DropdownMenu.ItemDecorationLeft
                    iconProps={{icon: 'photo-picture'}}
                  />
                  <DropdownMenu.ItemLabel>Add images</DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Trigger>
            {/* Network-access control. Shown only when the session has granted
                domains; clicking opens the manage/revoke view. Hidden when
                there are no grants for now — once the permissions dialog
                supports manual control (e.g. the user entering their own URL),
                show it unconditionally so it can also initiate a grant. */}
            {activeNetworkDomains.length > 0 && (
              <>
                <IconButton
                  ref={networkTriggerRef}
                  icon="link-alt"
                  variant="ghost"
                  isActive={isManagingNetwork}
                  aria-label="Manage network access"
                  tooltip={{children: 'Manage network access'}}
                  isDisabled={isRunning || isLocked}
                  onPress={() => setIsManagingNetwork(prev => !prev)}
                />
                <Dialog.Popover
                  isOpen={isManagingNetwork}
                  onOpenChange={setIsManagingNetwork}
                  triggerRef={networkTriggerRef}
                  placement="top start"
                  p={6}
                  aria-label="Manage network access">
                  {/* NOTE: Utilities scope as `.tw-style <utility>`, so the
                      width must sit on a child, not on `Tailwind`. */}
                  <Tailwind>
                    <div
                      className="w-[288px]"
                      style={{filter: nightModeFilter}}>
                      <WBAgentManageNetworkAccess
                        domains={activeNetworkDomains}
                        isDisabled={isRunning || isLocked}
                        onSubmit={kept => {
                          setIsManagingNetwork(false);
                          updateNetworkDomains(kept);
                        }}
                        onCancel={() => setIsManagingNetwork(false)}
                      />
                    </div>
                  </Tailwind>
                </Dialog.Popover>
              </>
            )}
            <WBAgentDebugViewToggle />
            <WBAgentForkButton />
            <WBAgentAgentSelector
              value={agentConfigOverride}
              onChange={setAgentConfigOverride}
            />
          </div>
          <div className="shrink-0">
            <ComposerAction
              isEmpty={
                isComposerEmpty &&
                draft.runs.length === 0 &&
                draft.files.length === 0
              }
              isLocked={isLocked}
              isCancelling={isCancelling}
              onSend={handleSend}
              onStop={trackTurnStopped}
            />
          </div>
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
};
